import {
  type Guideline,
  type GuidelineScope,
  type GuidelineUpdate,
  scopeHasCondition,
} from '@/shared/triage/guidelines';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  type EmailCategory,
  type TriageDestination,
} from '@/shared/triage/vocabulary';

/** What «Editar» of a criterion shows (contract v10.35). `''` is «no lo cambia». */
export interface GuidelineEditForm {
  text: string;
  scope: GuidelineScope;
  condition: string;
  appliesTo: EmailCategory[];
  category: EmailCategory | '';
  destination: TriageDestination | '';
}

export function guidelineEditForm(guideline: Guideline): GuidelineEditForm {
  return {
    text: guideline.text,
    scope: guideline.scope,
    condition: guideline.condition ?? '',
    appliesTo: inOrder(guideline.applies_to ?? []),
    category: guideline.category ?? '',
    destination: guideline.destination ?? '',
  };
}

/** The categories in the order of the vocabulary, so two selections compare as lists. */
export function inOrder(categories: readonly EmailCategory[]): EmailCategory[] {
  const chosen = new Set(categories);
  return CATEGORIES.filter((category) => chosen.has(category));
}

/**
 * Only what changed goes to the API: the PATCH changes the fields it receives and keeps
 * the rest. Text is compared trimmed and with its spaces folded, as the API stores it. The
 * condition only travels with a reach that has one (`remitente_si`, `general`): for the
 * other two the API clears it on its own, and sending it would be a change nobody made.
 */
export function guidelineChanges(guideline: Guideline, form: GuidelineEditForm): GuidelineUpdate {
  const change: GuidelineUpdate = {};
  const text = fold(form.text);
  if (text && text !== fold(guideline.text)) change.text = text;
  if (form.scope !== guideline.scope) change.scope = form.scope;
  if (scopeHasCondition(form.scope)) {
    const condition = fold(form.condition);
    if (condition !== fold(guideline.condition ?? '')) change.condition = condition || null;
  }
  const appliesTo = inOrder(form.appliesTo);
  if (appliesTo.join() !== inOrder(guideline.applies_to ?? []).join()) {
    change.applies_to = appliesTo;
  }
  const category = form.category || null;
  if (category !== (guideline.category ?? null)) change.category = category;
  const destination = form.destination || null;
  if (destination !== (guideline.destination ?? null)) change.destination = destination;
  return change;
}

function fold(value: string): string {
  return value.split(/\s+/).filter(Boolean).join(' ');
}

/** «Pedido sin albarán, Pedido ambiguo»; «Cualquiera» when it does not name any. */
export function appliesToText(categories: readonly EmailCategory[] | undefined): string {
  const named = inOrder(categories ?? []);
  return named.length > 0
    ? named.map((category) => CATEGORY_LABELS[category]).join(', ')
    : 'Cualquiera';
}

/**
 * What the criterion changes, in the panel's words: «Pedido sin albarán · Va a María»,
 * «Se queda en el buzón». «Nada» for one that only concerns its own email.
 */
export function effectText(guideline: Pick<Guideline, 'category' | 'destination'>): string {
  const parts: string[] = [];
  if (guideline.category) parts.push(CATEGORY_LABELS[guideline.category]);
  if (guideline.destination) {
    parts.push(
      guideline.destination === 'se_queda'
        ? 'Se queda en el buzón'
        : `Va a ${DESTINATION_LABELS[guideline.destination]}`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'Nada';
}

/** What a «Solo ese correo» criterion says instead of how it has done. */
export const ONE_EMAIL_OUTCOME = 'Solo cuenta para ese correo: no se aplica a otros';

/**
 * «Aplicado 7 veces · 3 confirmadas · 4 corregidas», and whether to look at it: more
 * corrected than confirmed means people keep undoing what it decides.
 */
export function outcomeText(
  guideline: Pick<Guideline, 'scope' | 'applied' | 'confirmed' | 'corrected'>,
): {
  text: string;
  worrying: boolean;
} {
  // «Solo ese correo» never reaches the triage of another email (the back only puts
  // `general`, `remitente` and `remitente_si` in front of the model): «Todavía no» would
  // promise something that is never going to happen.
  if (guideline.scope === 'este_correo') return { text: ONE_EMAIL_OUTCOME, worrying: false };
  const applied = guideline.applied ?? 0;
  const confirmed = guideline.confirmed ?? 0;
  const corrected = guideline.corrected ?? 0;
  if (applied === 0) return { text: 'Todavía no se ha aplicado', worrying: false };
  return {
    text: [
      `Aplicado ${applied} ${applied === 1 ? 'vez' : 'veces'}`,
      `${confirmed} ${confirmed === 1 ? 'confirmada' : 'confirmadas'}`,
      `${corrected} ${corrected === 1 ? 'corregida' : 'corregidas'}`,
    ].join(' · '),
    worrying: corrected > confirmed,
  };
}
